import { cn } from "@/lib/utils";
import { UserOrganizationRole } from "@blinkbooks/types";
import { ROLE_LABELS } from "@/lib/permissions";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Bot,
  ShoppingCart,
  CreditCard,
  Users,
  Banknote,
  Calculator,
  FileBarChart,
  TrendingUp,
  Activity,
  Plug,
  UsersRound,
  LayoutDashboard,
  Info,
  Pin,
  PinOff,
  ChevronRight,
  ChevronLeft,
  ChevronsUpDown,
  Check,
  Plus,
  Layers,
  Pencil,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useOrganization } from "@/contexts/OrganizationContext";
import { ViewModal } from "@/components/ViewModal";
import SidebarTooltip from "@/components/SidebarTooltip";

const envMode = import.meta.env.MODE;
const envDisplayName: Record<string, string> = { default: "Develop" };
const envLabel = envMode !== "production" ? (envDisplayName[envMode] ?? envMode) : null;

const envBgColor: Record<string, string> = {
  development: "bg-blue-600",
  staging: "bg-amber-500",
  test: "bg-purple-600",
};

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  unreadActivityCount?: number;
  company?: { id: string; name: string } | null;
  role?: UserOrganizationRole | null;
  isPinned?: boolean;
  onPinnedChange?: (pinned: boolean) => void;
  onExpandChange?: (expanded: boolean) => void;
}

interface SidebarItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  info?: string;
}

interface SidebarSection {
  label?: string;
  items: SidebarItem[];
}

const sidebarSections: SidebarSection[] = [
  {
    items: [
      {
        id: "ai-mode",
        label: "AI Mode (Ask Data)",
        icon: Bot,
        info: "Chat with your financial data using natural language. Ask questions like \"What were my top expenses last month?\" or \"Show me revenue trends\" and get instant, AI-powered answers backed by your real numbers.",
      },
      {
        id: "financial-control",
        label: "Financial KPIs Panel",
        icon: LayoutDashboard,
        info: "Your business's financial command center. Get a real-time, consolidated view of revenue, expenses, profit margins, outstanding invoices, and cash position - all in one place. Designed to give you instant clarity on whether your business is financially healthy, so you can make fast, confident decisions without digging through reports.",
      },
      {
        id: "for-you",
        label: "For You",
        icon: Activity,
        info: "Personalized alerts and signals based on your financial data. See what requires your attention — overdue invoices, financial warnings, and system health — all in one place.",
      },
    ],
  },
  {
    label: "Basic Overview",
    items: [
      {
        id: "sales-receivables",
        label: "Sales & Receivables",
        icon: ShoppingCart,
        info: "Track all income coming into your business - invoices issued, payments received, and outstanding balances owed by customers.",
      },
      {
        id: "expenses-payables",
        label: "Expenses & Payables",
        icon: CreditCard,
        info: "Monitor all outgoing costs - bills, recurring expenses, and amounts you owe to suppliers or service providers.",
      },
      {
        id: "vendors",
        label: "Vendors",
        icon: Users,
        info: "Manage your supplier relationships and see spending summaries, open balances, and payment history per vendor.",
      },
      {
        id: "banking",
        label: "Banking & Cards",
        icon: Banknote,
        info: "View and reconcile transactions across your connected bank accounts and credit cards.",
      },
    ],
  },
  {
    label: "Advanced",
    items: [
      {
        id: "accounting",
        label: "Financial Structure",
        icon: Calculator,
        info: "A deep view into your chart of accounts - the backbone of your bookkeeping. See how income, expenses, assets, liabilities, and equity accounts are structured, review account balances, and understand how every transaction maps to your financial architecture. Essential for understanding the \"why\" behind your numbers and ensuring your books are organized correctly.",
      },
      {
        id: "cashflow-forecast",
        label: "Cashflow & Forecast",
        icon: TrendingUp,
        info: "Understand where your cash stands today and where it's headed. This section combines your historical cash movements with forward-looking projections - factoring in expected income, scheduled payments, and recurring obligations. See week-by-week and month-by-month forecasts so you can anticipate shortfalls, plan for growth, and never be caught off guard by a cash crunch.",
      },
      {
        id: "reports-analytics",
        label: "Reports",
        icon: FileBarChart,
        info: "Financial statements consolidated across every connected entity, on one screen. Start with your Profit & Loss - drill from any line into its vendors, monthly trend, and individual transactions, with the same granularity available in your QuickBooks general ledger.",
      },
      {
        id: "activity",
        label: "Activity",
        icon: Activity,
        info: "A log of recent changes, syncs, and notable events in your account - useful for auditing and staying up to date.",
      },
    ],
  },
  {
    label: "Settings",
    items: [
      {
        id: "integrations",
        label: "Integrations",
        icon: Plug,
        info: "Connect and manage your accounting platforms, banks, and other data sources.",
      },
      {
        id: "team",
        label: "Team",
        icon: UsersRound,
        info: "Invite team members, manage roles, and control who has access to your financial data.",
      },
    ],
  },
];

const Sidebar = ({ activeTab, onTabChange, unreadActivityCount = 0, company, role, isPinned = false, onPinnedChange, onExpandChange }: SidebarProps) => {
  const navigate = useNavigate();
  const { organizations, views, activeOrganizationId, activeViewId, setActiveOrganizationId, setActiveView } = useOrganization();
  // "create" opens the modal in create mode; a view id opens it in edit mode for that view.
  const [viewModalTarget, setViewModalTarget] = useState<"create" | string | null>(null);
  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const [isExpanded, setIsExpanded] = useState(() => {
    if (isPinned) return true;
    const saved = localStorage.getItem('nav-sidebar-expanded');
    return saved === null ? true : saved === 'true';
  });
  const showExpanded = isPinned || isExpanded;

  // layoutExpanded lags showExpanded by 200ms when opening so content appears
  // after the width animation is mostly done; collapses immediately so text
  // disappears before the container starts shrinking.
  const [layoutExpanded, setLayoutExpanded] = useState(showExpanded);

  useEffect(() => {
    if (isPinned) setIsExpanded(true);
    else setIsExpanded(false);
  }, [isPinned]);

  useEffect(() => {
    onExpandChange?.(showExpanded);
  }, [showExpanded, onExpandChange]);

  useEffect(() => {
    if (showExpanded) {
      const t = setTimeout(() => setLayoutExpanded(true), 200);
      return () => clearTimeout(t);
    } else {
      setLayoutExpanded(false);
    }
  }, [showExpanded]);

  const toggleExpanded = () => {
    if (isPinned) return;
    setIsExpanded((v) => {
      const next = !v;
      localStorage.setItem('nav-sidebar-expanded', String(next));
      return next;
    });
  };

  return (
    <div
      className={cn(
        "bg-sidebar flex flex-col border-r transition-all duration-300 overflow-hidden relative w-full h-full",
        showExpanded ? "shadow-2xl" : "shadow-lg"
      )}
    >
      {/* Header */}
      <div className={cn(
        "border-b border-sidebar-hover/50",
        layoutExpanded ? "px-3 py-5" : "py-4 flex flex-col items-center gap-2"
      )}>
        {layoutExpanded ? (
          <div className="relative">
            <img
              src="/content/logo_with_text.png"
              alt="BlinkBooks"
              className="w-full h-auto object-contain pr-6"
            />
            <button
              onClick={() => onPinnedChange?.(!isPinned)}
              className="absolute right-0 top-0 p-1 rounded hover:bg-sidebar-hover text-[#FDF9EE]/40 hover:text-[#FDF9EE] transition-colors"
              title={isPinned ? "Unpin sidebar" : "Pin sidebar open"}
            >
              {isPinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
            </button>
            {envLabel && (
              <div className={cn(
                "text-center text-xs font-semibold uppercase tracking-widest text-white mt-1 px-2 py-1",
                envBgColor[envMode] ?? "bg-gray-600"
              )}>
                {envLabel}
              </div>
            )}
            {company && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="mt-3 flex items-center gap-2.5 w-full text-left rounded-md hover:bg-sidebar-hover/60 transition-colors p-1 -m-1">
                    <div className="flex-shrink-0 h-7 w-7 rounded-md bg-primary flex items-center justify-center text-[11px] font-bold text-white uppercase">
                      {activeView ? <Layers className="w-3.5 h-3.5" /> : company.name[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#FDF9EE] truncate leading-tight">{activeView ? activeView.name : company.name}</p>
                      {activeView ? (
                        <p className="text-[10px] text-[#FDF9EE]/60 leading-tight">View</p>
                      ) : (
                        role && <p className="text-[10px] text-[#FDF9EE]/60 leading-tight">{ROLE_LABELS[role]}</p>
                      )}
                    </div>
                    <ChevronsUpDown className="w-3.5 h-3.5 flex-shrink-0 text-[#FDF9EE]/40" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">Companies</DropdownMenuLabel>
                  {organizations.map((org) => (
                    <DropdownMenuItem
                      key={org.id}
                      onClick={() => {
                        if (org.id !== activeOrganizationId) setActiveOrganizationId(org.id);
                      }}
                      className="cursor-pointer gap-2"
                    >
                      <span className="flex-1 truncate">{org.name}</span>
                      {org.id === activeOrganizationId && <Check className="h-4 w-4 flex-shrink-0" />}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem onClick={() => navigate("/onboarding/organization")} className="cursor-pointer gap-2">
                    <Plus className="h-4 w-4" />
                    Add company
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">Views</DropdownMenuLabel>
                  {views.map((view) => (
                    <DropdownMenuItem
                      key={view.id}
                      onClick={() => {
                        if (view.id !== activeViewId) setActiveView(view.id);
                      }}
                      className="cursor-pointer gap-2"
                    >
                      <Layers className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate">{view.name}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewModalTarget(view.id);
                        }}
                        className="flex-shrink-0 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent-foreground/10"
                        title="Edit view"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {view.id === activeViewId && <Check className="h-4 w-4 flex-shrink-0" />}
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuItem onClick={() => setViewModalTarget("create")} className="cursor-pointer gap-2">
                    <Plus className="h-4 w-4" />
                    Add view
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        ) : (
          <>
            <img src="/content/logo-white.png" alt="BlinkBooks" className="w-8 h-8 object-contain" />
            {company && (
              <div
                className="h-7 w-7 rounded-md bg-primary flex items-center justify-center text-[11px] font-bold text-white uppercase mt-2"
                title={company.name}
              >
                {company.name[0]}
              </div>
            )}
          </>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2">
        <div className="space-y-1">
          {sidebarSections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {section.label && (
                <div className="h-px bg-sidebar-hover/30 mx-1 my-2" />
              )}
              <ul className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  const showBadge = item.id === "activity" && unreadActivityCount > 0;

                  const btn = (
                    <button
                      onClick={() => onTabChange(item.id)}
                      className={cn(
                        "w-full flex items-center transition-all duration-300 group relative border-l-2 py-3 rounded-lg",
                        showExpanded ? "px-4" : "justify-center px-2",
                        isActive
                          ? "bg-sidebar-hover border-primary rounded-r-lg"
                          : "border-transparent text-[#FDF9EE]/80 hover:bg-sidebar-hover hover:text-[#FDF9EE]"
                      )}
                    >
                      <div className="relative flex-shrink-0">
                        <Icon className={cn(
                          "w-5 h-5 transition-colors",
                          isActive ? "text-primary" : "text-[#FDF9EE]/60 group-hover:text-[#FDF9EE]"
                        )} />
                        {showBadge && !showExpanded && (
                          <span className="absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full">
                            {unreadActivityCount}
                          </span>
                        )}
                      </div>
                      {/* Text always in DOM — CSS drives the reveal so no layout snap */}
                      <span
                        className={cn(
                          "font-medium text-[15px] leading-tight whitespace-nowrap overflow-hidden",
                          "transition-[max-width,opacity,margin-left] duration-300 ease-in-out",
                          showExpanded
                            ? "max-w-[200px] opacity-100 ml-3.5"
                            : "max-w-0 opacity-0 ml-0",
                          isActive ? "text-[#FDF9EE]" : "text-[#FDF9EE]/80 group-hover:text-[#FDF9EE]"
                        )}
                      >
                        {item.label}
                      </span>
                      {showBadge && showExpanded && (
                        <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full shadow-sm ml-auto">
                          {unreadActivityCount}
                        </span>
                      )}
                    </button>
                  );

                  return (
                    <li key={item.id} className="group/item">
                      <div className="relative flex items-center">
                        {!showExpanded ? (
                          <SidebarTooltip label={item.label} info={item.info}>
                            {btn}
                          </SidebarTooltip>
                        ) : btn}
                        {item.info && layoutExpanded && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                onClick={(e) => e.stopPropagation()}
                                className="absolute right-2 opacity-0 group-hover/item:opacity-100 transition-opacity duration-150 p-1 rounded-full hover:bg-white/10 focus:outline-none"
                                aria-label={`Info about ${item.label}`}
                              >
                                <Info className="w-3.5 h-3.5 text-gray-400 hover:text-white" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent side="right" align="center" sideOffset={8} className="w-72 text-sm leading-relaxed">
                              <p className="font-semibold mb-1.5 text-foreground">{item.label}</p>
                              <p className="text-muted-foreground">{item.info}</p>
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      {/* Expand / collapse toggle — only when not pinned */}
      {!isPinned && (
        <div className={cn("border-t border-sidebar-hover/50 flex", showExpanded ? "justify-end px-3 py-2" : "justify-center py-2")}>
          <button
            onClick={toggleExpanded}
            title={showExpanded ? "Collapse sidebar" : "Expand sidebar"}
            className="p-1.5 rounded-lg text-[#FDF9EE]/40 hover:text-[#FDF9EE] hover:bg-sidebar-hover transition-colors"
          >
            {showExpanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      )}

      {/* Footer */}
      {layoutExpanded && (
        <div className="border-t border-sidebar-hover/50 px-4 py-3 flex justify-center gap-3">
          <button
            onClick={() => navigate('/license-agreement')}
            className="text-xs text-[#FDF9EE]/40 hover:text-[#FDF9EE]/70 transition-colors underline-offset-4 hover:underline"
          >
            License Agreement
          </button>
          <span className="text-[#FDF9EE]/20 text-xs">•</span>
          <button
            onClick={() => navigate('/privacy-policy')}
            className="text-xs text-[#FDF9EE]/40 hover:text-[#FDF9EE]/70 transition-colors underline-offset-4 hover:underline"
          >
            Privacy Policy
          </button>
        </div>
      )}

      <ViewModal
        open={viewModalTarget !== null}
        viewId={viewModalTarget !== null && viewModalTarget !== "create" ? viewModalTarget : undefined}
        onClose={() => setViewModalTarget(null)}
      />
    </div>
  );
};

export default Sidebar;
